import type { QuoteError } from '../types/quote.types';

/** quote-service's QuoteController::CODE_DEALER_PRODUCT_NOT_AUTHORISED. */
export const DEALER_PRODUCT_NOT_AUTHORISED = 'dealer_product_not_authorised';

/**
 * Names the dealer and product on quote-service's dealer/product combination
 * refusal. Gated on that response's stable code, NOT on status alone: a
 * `permission:quote.add` / `quote.edit` denial is also a 403 and would otherwise
 * be misreported as an incompatible dealer choice. Every other case falls
 * through to the server's own message unchanged. A name is never fabricated.
 */
export const resolveQuoteErrorMessage = (
    error: { message?: string; status?: number; code?: string } | null,
    context: { dealerName?: string | null; productName?: string | null },
): string => {
    const fallback = error?.message || 'An unexpected error occurred.';

    if (error?.status !== 403) return fallback;
    if (error.code !== DEALER_PRODUCT_NOT_AUTHORISED) return fallback;
    if (!context.dealerName || !context.productName) return fallback;

    return `You are not authorised to sell ${context.productName} through ${context.dealerName}. `
        + 'Choose a different dealer, or go back and choose a different product.';
};

const FALLBACK_SAVE_ERROR = 'An unexpected error occurred while saving the quote.';

/** Reads the fields the error modal renders off any thrown value, without trusting its shape. */
export const toQuoteError = (error: unknown): QuoteError => {
    if (typeof error !== 'object' || error === null) {
        return { message: FALLBACK_SAVE_ERROR };
    }
    const record = error as Record<string, unknown>;
    const message = typeof record.message === 'string' && record.message ? record.message : FALLBACK_SAVE_ERROR;
    return {
        message,
        status: typeof record.status === 'number' ? record.status : undefined,
        code: typeof record.code === 'string' ? record.code : undefined,
        data: record.data,
    };
};

const TITLES: Record<number, string> = {
    403: 'Access Denied',
    409: 'Quote Changed',
    422: 'Validation Error',
    500: 'Server Error',
};

/** Retrying cannot clear a refused permission or a stale version. */
export const quoteErrorPresentation = (status?: number): { title: string; canRetry: boolean } => ({
    title: (status !== undefined && TITLES[status]) || 'Quote Save Failed',
    canRetry: status !== 403 && status !== 409,
});
